// Overlay translated strings onto the raw DynamoDB `elements` map of a template
// so the whole downstream pipeline (rbmElements → rbmFlows → PhonePreview /
// InteractiveDemo) renders the selected language transparently. Postbacks,
// URLs, and routing metadata are never touched. Missing translations fall
// back to the base (Spanish) copy element-by-element.

export const SUPPORTED_LANGS = ['es', 'ca', 'gl', 'eu', 'fr', 'en']

export const LANG_LABELS = {
  es: 'Castellano',
  ca: 'Català',
  gl: 'Galego',
  eu: 'Euskera',
  fr: 'Français',
  en: 'English'
}

export const parseTemplateI18n = (raw) => {
  if (!raw) return null
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch (e) {
    console.warn('[templateI18n] parse failed:', e && e.message)
    return null
  }
}

const overlayElement = (el, tr) => {
  if (!el || !tr) return el
  const rcs = el.RCSMessage
  if (!rcs) return el
  if (tr.textMessage && 'textMessage' in rcs) {
    rcs.textMessage = tr.textMessage
  }
  const chips =
    rcs.suggestedChipList &&
    Array.isArray(rcs.suggestedChipList.suggestions) &&
    rcs.suggestedChipList.suggestions
  if (chips && Array.isArray(tr.suggestions)) {
    chips.forEach((sug, idx) => {
      const t = tr.suggestions[idx]
      if (!t) return
      if (sug.reply) sug.reply.displayText = t
      else if (sug.action) sug.action.displayText = t
    })
  }
  const rich = rcs.richCard || rcs.richcardMessage
  const cards = []
  if (rich && rich.standaloneCard && rich.standaloneCard.cardContent) {
    cards.push(rich.standaloneCard.cardContent)
  }
  if (rich && rich.carouselCard && Array.isArray(rich.carouselCard.cardContents)) {
    cards.push(...rich.carouselCard.cardContents)
  }
  if (cards.length && Array.isArray(tr.cards)) {
    cards.forEach((card, idx) => {
      const trCard = tr.cards[idx]
      if (!trCard) return
      if (trCard.title) card.title = trCard.title
      if (trCard.description) card.description = trCard.description
      if (Array.isArray(trCard.suggestions) && Array.isArray(card.suggestions)) {
        card.suggestions.forEach((sug, j) => {
          const t = trCard.suggestions[j]
          if (!t) return
          if (sug.reply) sug.reply.displayText = t
          else if (sug.action) sug.action.displayText = t
        })
      }
    })
  }
  return el
}

// Apply i18n overlay to the whole `elements` map. Returns a NEW map (deep
// clone) so React memoization on template.elements is preserved.
export const applyI18nToElements = (elements, lang, i18n) => {
  if (!elements) return elements
  if (!lang || lang === 'es' || !i18n || !i18n[lang]) return elements
  // Deep clone so we don't mutate the source (avoids cross-render leaks).
  const cloned = JSON.parse(JSON.stringify(elements))
  const langMap = i18n[lang]
  Object.keys(cloned).forEach((key) => {
    const tr = langMap[key]
    if (!tr) return
    overlayElement(cloned[key], tr)
  })
  return cloned
}

// Which langs actually carry any translation for this template. Always
// prepend 'es' (the base). Used by the language selector to show only
// options that make sense for the current template.
export const availableLangsForTemplate = (i18n) => {
  const set = new Set(['es'])
  if (i18n && typeof i18n === 'object') {
    Object.keys(i18n).forEach((k) => {
      if (SUPPORTED_LANGS.includes(k) && i18n[k] && Object.keys(i18n[k]).length) {
        set.add(k)
      }
    })
  }
  return SUPPORTED_LANGS.filter((l) => set.has(l))
}
